/**
 * Pure metadata-dispatch planner.
 *
 * The renderer's settings->props mapper is intentionally pure (no data access),
 * so metadata-block computation -- which needs the data rows -- lives here in the
 * app layer. Given the persisted phylocanvas settings bag, the metadata sync
 * engine, and the metadata rows, this returns a plain plan the app fans out
 * through the renderer's existing setters. M1 populates only the block-related
 * keys; later slices fill `colorBy` (M2), `showPiecharts` (M3), `legend` (M4),
 * and `tooltipFields` (M5).
 */

import { buildLeafColorMap } from './color-by-leaves';

type MetadataBlocks = Record<string, Record<string, { label: string; colour: string }>>;

/**
 * The slice of `TreeMetadataSync` this helper uses, expressed structurally so the
 * helper neither imports nor couples to the singleton. The real engine is
 * assignable to this; tests inject the real engine to exercise the true palette
 * and hide-null logic.
 */
export interface MetadataSyncEngine {
  state: { hideNullBlocks: boolean; labelField: string | null };
  // Block-palette selection is temporal: `generateColorMap` reads these at call
  // time (getCategoricalColors), so they are set per field just before each call.
  currentPaletteCategory: string;
  currentPaletteName: string;
  generateColorMap(
    metadata: Record<string, unknown>[],
    field: string,
    columnTypes: Record<string, unknown>,
  ): Map<string, string>;
  generateLeafColorMap(
    metadata: Record<string, unknown>[],
    field: string,
    columnTypes: Record<string, unknown>,
  ): Map<string, string>;
  setLeafPalette(category: string, name: string): void;
  computeMetadataBlocks(fields: string[]): MetadataBlocks;
}

export interface MetadataDispatchData {
  metadata: Record<string, unknown>[];
  columnTypes: Record<string, unknown>;
}

export interface MetadataDispatchPlan {
  fields: string[];
  blocks: MetadataBlocks;
  blockSize: number | undefined;
  hideNull: boolean;
  colorBy: { field: string; leafColorMap: Record<string, string> } | null;
  showPiecharts: boolean | null;
  legend: { visible: boolean; columns: number; position: string } | null;
  tooltipFields: string[];
}

function availableFields(metadata: Record<string, unknown>[]): Set<string> {
  const first = metadata[0];
  if (!first) return new Set();
  return new Set(Object.keys(first).filter((k) => k !== '__rowIndex'));
}

/**
 * The persisted per-field palette choices (`field -> ColorBrewer name`).
 * Phylocanvas saves this into the settings bag as a plain object; absent or
 * malformed entries are dropped so each field falls back to the default palette.
 */
function fieldPalettes(phylo: Record<string, unknown>): Record<string, string> {
  const raw = phylo.fieldPalettes;
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [field, name] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof name === 'string') out[field] = name;
  }
  return out;
}

/**
 * Resolve the colour-by palette NAME for `field` from the canonical phylocanvas
 * bag, mirroring phylocanvas's own load (phylocanvas-renderer.ts:4150): the
 * `colorByPalette.name`, then the field's per-field palette, then the Set1
 * default. The SVG appearance panel reads the same bag on a renderer switch, so
 * sharing this resolver keeps its colour-by palette in step with phylocanvas.
 * The palette category is always qualitative for colour-by, so callers hold that
 * constant and only the name varies.
 */
export function resolveColorByPaletteName(phylo: Record<string, unknown>, field: string): string {
  const colorByPalette = phylo.colorByPalette;
  const named =
    colorByPalette && typeof colorByPalette === 'object'
      ? (colorByPalette as { name?: unknown }).name
      : undefined;
  if (typeof named === 'string' && named) return named;
  return fieldPalettes(phylo)[field] || 'Set1';
}

/**
 * The persisted per-field block palettes as the `Map<field, name>` phylocanvas
 * keeps in memory. Mirrors phylocanvas's own load (`new Map(Object.entries(...))`,
 * phylocanvas-renderer.ts:4035) but reuses the sanitising reader above so malformed
 * entries are dropped. The standalone switch path re-seeds the phylocanvas instance
 * with this, so its switch-out snapshot preserves the block palettes instead of
 * flushing an empty map over them.
 */
export function fieldPalettesMap(phylo: Record<string, unknown>): Map<string, string> {
  return new Map(Object.entries(fieldPalettes(phylo)));
}

/**
 * Resolve the persisted `colorByField` into the `leafLabel -> hex` map the SVG
 * renderer's `setLeafColorMap` consumes. Returns null unless the field is a
 * non-empty string present in the data (a stale/removed field is ignored).
 * Mirrors the standalone panel's recipe -- the field's persisted palette
 * (default Set1), `generateLeafColorMap` for the value->hex map, then the tested
 * `buildLeafColorMap` reshape, which skips leaves whose value is empty or absent
 * from the palette.
 */
function planColorBy(
  phylo: Record<string, unknown>,
  sync: MetadataSyncEngine,
  data: MetadataDispatchData,
  available: Set<string>,
  palettes: Record<string, string>,
): { field: string; leafColorMap: Record<string, string> } | null {
  const field = typeof phylo.colorByField === 'string' ? phylo.colorByField : '';
  if (!field || !available.has(field)) return null;

  sync.setLeafPalette('qualitative', palettes[field] || 'Set1');
  const valueColors = sync.generateLeafColorMap(data.metadata, field, data.columnTypes);
  const labelField = sync.state.labelField || 'id';
  return {
    field,
    leafColorMap: buildLeafColorMap(data.metadata, labelField, field, valueColors),
  };
}

/**
 * Resolve the persisted legend settings the renderer-agnostic overlay consumes.
 * Defaults mirror phylocanvas's embedded legend (visible, two columns, top-left
 * corner). `legendVisible` is the current key; `showLegend` is the legacy
 * fallback. Column and position values are validated downstream in the overlay,
 * so the planner passes the raw saved choices straight through.
 */
/**
 * Resolve the persisted `tooltipFields` (which metadata values a leaf's hover
 * tooltip shows) to the fields actually present in the data. An empty result
 * carries phylocanvas's show-all semantics -- the renderer then shows every
 * value -- so non-array bags and stale/removed fields collapse to `[]`.
 */
function planTooltipFields(phylo: Record<string, unknown>, available: Set<string>): string[] {
  if (!Array.isArray(phylo.tooltipFields)) return [];
  return (phylo.tooltipFields as unknown[]).filter(
    (f): f is string => typeof f === 'string' && available.has(f),
  );
}

function planLegend(phylo: Record<string, unknown>): MetadataDispatchPlan['legend'] {
  const visible =
    typeof phylo.legendVisible === 'boolean'
      ? phylo.legendVisible
      : typeof phylo.showLegend === 'boolean'
        ? phylo.showLegend
        : true;
  const columns = typeof phylo.legendColumns === 'number' ? phylo.legendColumns : 2;
  const position = typeof phylo.legendPosition === 'string' ? phylo.legendPosition : 'top-left';
  return { visible, columns, position };
}

export function planMetadataDispatch(
  phylo: Record<string, unknown>,
  sync: MetadataSyncEngine,
  data: MetadataDispatchData,
): MetadataDispatchPlan {
  const available = availableFields(data.metadata);
  const requested = Array.isArray(phylo.selectedMetadataFields)
    ? (phylo.selectedMetadataFields as unknown[]).filter(
        (f): f is string => typeof f === 'string',
      )
    : [];
  const fields = requested.filter((f) => available.has(f));

  // The persisted bag is the single source of truth for hide-null; default
  // `true` for parity with phylocanvas's embedded default (hide empty cells).
  // The SVG renderer's own `hideNullBlocks` field defaults `false` and is not
  // consulted on the Tableau path.
  const hideNull = typeof phylo.hideNullBlocks === 'boolean' ? phylo.hideNullBlocks : true;
  sync.state.hideNullBlocks = hideNull;

  // Ensure every selected field has a colour map, else computeMetadataBlocks
  // falls back to grey (#cccccc) for each cell. generateColorMap also sets the
  // transient colorByField/colorMap, which only colour-by (M2) reads -- benign
  // for block rendering here. Per-field palette (M3): set the block palette just
  // before each generate, the same temporal pattern phylocanvas uses
  // (phylocanvas-renderer.ts:2508); default Set1 when none is persisted.
  const palettes = fieldPalettes(phylo);
  fields.forEach((field) => {
    sync.currentPaletteCategory = 'qualitative';
    sync.currentPaletteName = palettes[field] || 'Set1';
    sync.generateColorMap(data.metadata, field, data.columnTypes);
  });

  const blocks = fields.length > 0 ? sync.computeMetadataBlocks(fields) : {};

  const blockSize =
    typeof phylo.blockSize === 'number' && Number.isFinite(phylo.blockSize)
      ? phylo.blockSize
      : undefined;

  return {
    fields,
    blocks,
    blockSize,
    hideNull,
    colorBy: planColorBy(phylo, sync, data, available, palettes),
    showPiecharts: typeof phylo.showPiecharts === 'boolean' ? phylo.showPiecharts : null,
    legend: planLegend(phylo),
    tooltipFields: planTooltipFields(phylo, available),
  };
}
